'use client';

import { useEffect, useRef, useState } from 'react';

import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import CopyToClipboard from 'react-copy-to-clipboard';

import styles from './EditorShareView.module.css';

import type { ShareModalClientProps } from './ShareModalClient';

function closeModal() {
  window.parent.postMessage(JSON.stringify({ action: 'feedbackclose' }), '*');
}

export function EditorShareView({
  shareUrl,
  commentsCount,
  unreadCount,
  ownerUrl,
}: Pick<
  ShareModalClientProps,
  'shareUrl' | 'commentsCount' | 'unreadCount' | 'ownerUrl'
>) {
  const t = useTranslations();
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    },
    [],
  );

  function handleCopy(_text: string, result: boolean) {
    if (!result) return;

    setCopied(true);
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = setTimeout(() => {
      copiedTimeoutRef.current = null;
      setCopied(false);
    }, 1000);
  }

  return (
    <div
      data-hook="share-lb"
      className="flex w-full min-h-screen flex-col border-t-[6px] border-[#116dff] bg-background"
    >
      {/* Header */}
      <div className="flex items-start justify-between px-6 py-5">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {t('share-editor-title')}
          </h2>
          <p className="mt-1 text-[16px] leading-6 text-muted-foreground">
            {t('share-subtitle')}
          </p>
        </div>
        <button
          type="button"
          data-hook="share-close"
          onClick={closeModal}
          className="text-muted-foreground hover:text-foreground"
          aria-label={t('share-cancel')}
        >
          <X size={20} />
        </button>
      </div>

      <hr className="border-[#d9e1e8]" />

      {/* Comments notification */}
      {commentsCount > 0 ? (
        <>
          <div
            data-hook="notifications"
            className="flex items-center gap-3 px-6 py-7"
          >
            <div aria-hidden="true" className={styles.talkBubble} />
            <span className="text-[16px] text-[#3b4057]">
              {unreadCount
                ? t('share-newCommentsNotification', {
                    count: unreadCount,
                  })
                : t('share-commentsNotification', {
                    count: commentsCount,
                  })}
            </span>
            {ownerUrl ? (
              <button
                type="button"
                data-hook="notifications-cta"
                onClick={() => window.open(ownerUrl, '_blank')}
                className="ml-auto shrink-0 text-sm text-[#116dff] hover:underline"
              >
                {t('share-viewComments')}
              </button>
            ) : null}
          </div>
          <hr className="border-[#d9e1e8]" />
        </>
      ) : null}

      {/* Browser-style address bar + copy button */}
      {shareUrl ? (
        <div
          className="flex items-center px-6"
          style={{ padding: '30px 24px' }}
        >
          <div className="flex w-[488px] max-w-full items-center gap-1 rounded bg-[#d9e1e8] px-2 py-1">
            <div aria-hidden="true" className={styles.miniToolbar} />
            <div className="mx-1 min-w-0 flex-1 rounded bg-white px-2 py-[3px]">
              <span
                data-hook="share-link"
                className="block truncate text-[18px] text-[#7a7a7a]"
                title={shareUrl}
              >
                {shareUrl}
              </span>
            </div>
          </div>
          <CopyToClipboard text={shareUrl} onCopy={handleCopy}>
            <button
              type="button"
              data-hook="copy-link"
              className="ml-auto shrink-0 rounded-full bg-[#e7f0ff] px-4 py-1.5 text-sm font-medium text-[#116dff] hover:bg-[#d6e5ff]"
            >
              {copied ? t('share-copied') : t('share-copy')}
            </button>
          </CopyToClipboard>
        </div>
      ) : null}

      {/* Footer */}
      <div className="mt-auto flex justify-end px-6 py-4">
        <button
          type="button"
          data-hook="share-close-footer"
          onClick={closeModal}
          className="rounded-full bg-[#116dff] px-5 py-2 text-sm text-white hover:opacity-90"
        >
          {t('share-close')}
        </button>
      </div>
    </div>
  );
}
